// The one fetch behind every record-derived data overlay (TGCLOUD-1670).
//
// Deliberately lazy: a pedigree that never opens the Overlays popover never
// pays for this. `enabled` is false until the popover opens or a slot is on.
//
// Cached under the family root id, so the two overlay slots, the picker's live
// counts and the criteria popover's preview all read ONE payload. Sixty seconds
// of staleness is right for a chart a clinician reads for minutes at a time;
// anything that actually changes a record invalidates explicitly below rather
// than waiting for it to expire.

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { useEntityChangeSubscription } from 'src/hooks/useEntityChangeSubscription';

export const overlayFactsKey = (rootId) => ['pedigreeOverlayFacts', rootId];

const fetchFacts = async (rootId) => {
  const url = APICONSTANTS.pedigreeOverlayFacts.replace(':user_id', rootId);
  const { data } = await axios.get(url);
  return data;
};

/**
 * @param {string} rootId   the family root user _id, the key the pedigree
 *                          routes already address a family by
 * @param {object} options
 * @param {boolean} options.enabled  fetch at all
 * @param {string}  options.familyId for the live change subscription
 */
export default function useOverlayFacts(
  rootId,
  { enabled = false, familyId } = {}
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: overlayFactsKey(rootId),
    queryFn: () => fetchFacts(rootId),
    enabled: Boolean(enabled && rootId),
    staleTime: 60_000,
    // A gap-spotting overlay that quietly retries a 403 forever is worse than
    // one that says it could not read the records.
    retry: 1
  });

  const invalidate = React.useCallback(() => {
    if (!rootId) return;
    queryClient.invalidateQueries({ queryKey: overlayFactsKey(rootId) });
  }, [queryClient, rootId]);

  // Saving any clinical note in the sidebar already fires this window event to
  // make the notes list refetch. An overlay reading the same records has to move
  // with it, or a clinician adds the contact the overlay just told them was
  // missing and the mark stays put.
  React.useEffect(() => {
    if (!enabled) return undefined;
    const events = ['clinical-notes-refresh', 'clinical-records-changed'];
    events.forEach((name) => window.addEventListener(name, invalidate));
    return () =>
      events.forEach((name) => window.removeEventListener(name, invalidate));
  }, [enabled, invalidate]);

  // Server-side writes that no sidebar action produced: today only the test PUT
  // emits, which is exactly the auto-propagation case a clinician would never
  // think to refresh for.
  useEntityChangeSubscription({
    familyId: enabled ? familyId : undefined,
    onChange: invalidate
  });

  return {
    facts: query.data || null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error || null,
    // The endpoint answers 200 with this rather than an error when the caller's
    // role may not read clinical records, so an absence overlay is never
    // evaluated against a payload that is empty for the wrong reason.
    unavailable: query.data?.unavailable || null,
    refresh: invalidate
  };
}
